import { forwardRef } from 'react'
import styles from './MetricEvidencePopover.module.css'

/**
 * MetricEvidencePopover — floating panel anchored beneath a metric card's value,
 * showing the metric's evidence. Visibility, hover-intent and pinning are owned by
 * the parent (MetricCard); this component only renders the panel and forwards the
 * hover handlers so moving the pointer into it keeps it open.
 *
 * Each evidence line is newline-delimited and rendered as one line of text.
 *
 * Props:
 *   header       {string}   — heading (the metric's evidence_header)
 *   evidence     {string}   — newline-delimited evidence text
 *   onMouseEnter {function} — re-assert "open" when the pointer enters the panel
 *   onMouseLeave {function} — schedule close when the pointer leaves the panel
 *   onClick      {function} — pin the panel open when it's clicked
 *
 * Ref: forwarded to the panel element so the parent can focus it when pinned.
 */
const MetricEvidencePopover = forwardRef(function MetricEvidencePopover(
  { header, evidence, onMouseEnter, onMouseLeave, onClick }, ref,
) {
  const lines = (evidence ?? '').split('\n').map(l => l.trim()).filter(Boolean)

  return (
    <div
      ref={ref}
      className={styles.popover}
      role="dialog"
      aria-label={header || 'Evidence'}
      tabIndex={-1}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {header && <div className={styles.header}>{header}</div>}

      <div className={styles.body}>
        {lines.map((line, i) => (
          <p key={i} className={styles.line}>{line}</p>
        ))}
      </div>
    </div>
  )
})

export default MetricEvidencePopover
